import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import moment from 'moment';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {KeyboardDatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {SaveOutlined, SearchOutlined} from '@material-ui/icons';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import CharacterCounter from '../../../shared/charactercounter/CharacterCounter.jsx';
import DialogSelecionarFuncionario from './DialogSelecionarFuncionario.jsx';
import {
    fimNaoAnteriorAoInicio,
    FORMATO_DATA_TELA,
    MENSAGEM_FIM_ANTERIOR_AO_INICIO,
    montarPayloadCoordenador,
    paraFormularioCoordenador,
    TAMANHO_MAXIMO_CARGO,
    TAMANHO_MAXIMO_MOTIVO,
    VALORES_VAZIOS_COORDENADOR
} from '../../components/cadastro/cadastroAtendimento.js';
import useFetch from '../../../../hooks/useFetch.js';
import {URL_COORDENADORES_CAMPI} from '../../../../constants/index.js';
import {tituloSecao} from '../../../../utils/styles.js';

const PARAMS_VAZIOS = {};

const dataValida = valor => !!valor && moment.isMoment(valor) && valor.isValid();

const FormSchema = Yup.object().shape({
    idPessoa: Yup.string().required('Selecione o funcionário'),
    idCampus: Yup.string().required('É obrigatório'),
    cargo: Yup.string().trim()
        .required('É obrigatório')
        .max(TAMANHO_MAXIMO_CARGO, `Máximo de ${TAMANHO_MAXIMO_CARGO} caracteres`),
    dataInicioVigencia: Yup.mixed()
        .test('obrigatoria', 'É obrigatória', valor => !!valor)
        .test('valida', 'Data inválida', valor => !valor || dataValida(valor)),
    dataFimVigencia: Yup.mixed()
        .nullable()
        .test('valida', 'Data inválida', valor => !valor || dataValida(valor))
        .test('posterior', MENSAGEM_FIM_ANTERIOR_AO_INICIO, function posterior(valor) {
            return !dataValida(valor) || !dataValida(this.parent.dataInicioVigencia)
                || fimNaoAnteriorAoInicio(this.parent.dataInicioVigencia, valor);
        }),
    motivoFimVigencia: Yup.string()
        .max(TAMANHO_MAXIMO_MOTIVO, `Máximo de ${TAMANHO_MAXIMO_MOTIVO} caracteres`)
});

const useStyles = makeStyles(theme => ({
    paper: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    tituloBloco: {
        ...tituloSecao(theme)
    },
    legenda: {
        ...theme.typography.body2,
        color: theme.palette.text.secondary
    }
}));

/*
 * Cria ou edita um Coordenador da Central de Bolsas. `item` vem preenchido na edição e
 * nulo na criação. A pessoa é escolhida num segundo diálogo, aberto por cima deste: idPessoa
 * e nome ficam guardados no formulário, então os demais campos não se perdem. Ao salvar
 * devolve o corpo do back; quem grava (e fecha o diálogo) é a tela.
 */
export default function DialogCoordenador({open, onClose, item, salvando, onSalvar}) {
    const classes = useStyles();
    const [fetchCampi] = useFetch({returnInnerLoadingState: true});
    const [campi, setCampi] = useState([]);
    const [selecionandoFuncionario, setSelecionandoFuncionario] = useState(false);
    // Na edição, o campus gravado entra como opção até a lista chegar (evita valor fora das opções no Select).
    const campusItem = item && item.campus;
    const opcoesCampus = campusItem && !campi.some(campus => campus.id === campusItem.id) ? [...campi, campusItem] : campi;
    const {register, control, handleSubmit, reset, watch, setValue, formState: {errors}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_VAZIOS_COORDENADOR
    });

    const preencher = () => reset(paraFormularioCoordenador(item));

    useEffect(() => {
        if (open) {
            preencher();
            fetchCampi(URL_COORDENADORES_CAMPI, PARAMS_VAZIOS, dados => setCampi(Array.isArray(dados) ? dados : []));
        }
    }, [open, item]);

    const selecionarFuncionario = funcionario => {
        setValue('idPessoa', String(funcionario.idPessoa), {shouldValidate: true, shouldDirty: true});
        setValue('nomePessoa', funcionario.nome || '');
    };

    const submit = values => onSalvar(montarPayloadCoordenador(values));

    const campoData = (nome, rotulo, {obrigatorio = false} = {}) => (
        <LabelValue label={obrigatorio ? `${rotulo} *` : rotulo}>
            <Controller
                name={nome}
                control={control}
                render={({field}) => (
                    <KeyboardDatePicker
                        value={field.value}
                        onChange={data => field.onChange(data)}
                        onBlur={field.onBlur}
                        format={FORMATO_DATA_TELA}
                        placeholder='dd/mm/aaaa'
                        mask='__/__/____'
                        cancelLabel='Cancelar'
                        okLabel='OK'
                        clearable={!obrigatorio}
                        clearLabel='Limpar'
                        invalidDateMessage='Data inválida'
                        inputVariant='outlined'
                        fullWidth
                        error={!!errors[nome]}
                        helperText={errors[nome]?.message}/>
                )}/>
        </LabelValue>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                TransitionProps={{onEntering: preencher}}
                scroll='body'
                aria-labelledby='dialog-coordenador-titulo'
                classes={{paper: classes.paper}}>
                <DialogTitle id='dialog-coordenador-titulo'>
                    {item ? 'Editar coordenador' : 'Adicionar coordenador'}
                </DialogTitle>
                <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                    <form noValidate autoComplete='off' onSubmit={handleSubmit(submit)}>
                        <DialogContent classes={{root: classes.contentRoot}}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <LabelValue label='Pessoa *'>
                                        <FormControl variant='outlined' fullWidth error={!!errors.idPessoa}>
                                            <OutlinedInput
                                                {...register('nomePessoa')}
                                                readOnly
                                                placeholder='Nenhum funcionário selecionado'
                                                error={!!errors.idPessoa}
                                                inputProps={{'aria-label': 'Pessoa'}}
                                                endAdornment={(
                                                    <InputAdornment position='end'>
                                                        <TextButton
                                                            startIcon={<SearchOutlined/>}
                                                            onClick={() => setSelecionandoFuncionario(true)}>
                                                            Selecionar
                                                        </TextButton>
                                                    </InputAdornment>
                                                )}/>
                                            {!!errors.idPessoa && <FormHelperText error>{errors.idPessoa.message}</FormHelperText>}
                                        </FormControl>
                                    </LabelValue>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LabelValue label='Campus *'>
                                        <FormControl variant='outlined' fullWidth error={!!errors.idCampus}>
                                            <Controller
                                                name='idCampus'
                                                control={control}
                                                render={({field}) => (
                                                    <Select {...field} displayEmpty>
                                                        <MenuItem value='' disabled><em>Selecione...</em></MenuItem>
                                                        {opcoesCampus.map(campus => (
                                                            <MenuItem key={campus.id} value={String(campus.id)}>{campus.nome}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}/>
                                            {!!errors.idCampus && <FormHelperText error>{errors.idCampus.message}</FormHelperText>}
                                        </FormControl>
                                    </LabelValue>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LabelValue label='Cargo *'>
                                        <FormControl variant='outlined' fullWidth>
                                            <OutlinedInput
                                                {...register('cargo')}
                                                error={!!errors.cargo}
                                                inputProps={{maxLength: TAMANHO_MAXIMO_CARGO}}/>
                                            {!!errors.cargo && <FormHelperText error>{errors.cargo.message}</FormHelperText>}
                                        </FormControl>
                                        <CharacterCounter characterQuantityMax={TAMANHO_MAXIMO_CARGO} value={watch('cargo')}/>
                                    </LabelValue>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {campoData('dataInicioVigencia', 'Início de vigência', {obrigatorio: true})}
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography component='h3' className={classes.tituloBloco}>
                                        Encerramento de vigência{' '}
                                        <span className={classes.legenda}>(preencher somente para concluir a vigência)</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {campoData('dataFimVigencia', 'Fim de vigência')}
                                </Grid>
                                <Grid item xs={12}>
                                    <LabelValue label='Motivo do fim'>
                                        <FormControl variant='outlined' fullWidth>
                                            <OutlinedInput
                                                {...register('motivoFimVigencia')}
                                                multiline
                                                minRows={3}
                                                error={!!errors.motivoFimVigencia}
                                                inputProps={{maxLength: TAMANHO_MAXIMO_MOTIVO}}/>
                                            {!!errors.motivoFimVigencia && (
                                                <FormHelperText error>{errors.motivoFimVigencia.message}</FormHelperText>
                                            )}
                                        </FormControl>
                                        <CharacterCounter characterQuantityMax={TAMANHO_MAXIMO_MOTIVO} value={watch('motivoFimVigencia')}/>
                                    </LabelValue>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <TextButton onClick={onClose}>Fechar</TextButton>
                            <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={salvando}>
                                Salvar
                            </ContainedButton>
                        </DialogActions>
                    </form>
                </MuiPickersUtilsProvider>
            </Dialog>
            <DialogSelecionarFuncionario
                open={selecionandoFuncionario}
                onClose={() => setSelecionandoFuncionario(false)}
                onSelecionar={selecionarFuncionario}/>
        </>
    );
}

DialogCoordenador.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    item: PropTypes.object,
    salvando: PropTypes.bool,
    onSalvar: PropTypes.func.isRequired
};
